import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge } from "./ui";

export default function Threats() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const canResolve = user.role === "admin" || user.role === "compliance";
  const [rows, setRows] = useState([]);
  const [workloads, setWorkloads] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", severity: "high", workload: "", description: "" });

  const load = () => api.get("/threats").then(({ data }) => setRows(data));
  useEffect(() => {
    load();
    api.get("/workloads").then(({ data }) => setWorkloads(data));
  }, []);

  const resolve = async (id) => {
    try {
      await api.post(`/threats/${id}/resolve`);
      toast.success("Threat marked resolved");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const report = async () => {
    if (!form.name || !form.workload) { toast.error("Name and target are required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/threats", form);
      if (["critical", "high"].includes(form.severity)) {
        toast.success(data.alert_sent ? "Threat logged · alert email sent to your team" : "Threat logged · alert email skipped (Resend not configured)");
      } else {
        toast.success("Threat logged");
      }
      setOpen(false);
      setForm({ name: "", severity: "high", workload: "", description: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const active = rows.filter((t) => t.status === "active");

  return (
    <div>
      <PageHeader eyebrow="CYBER PROTECTION" title="Threat Defense" subtitle="Behavioral detection across every protected asset">
        {isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="report-threat-btn" className="rounded-none bg-primary hover:bg-primary/90 text-white"><Plus className="h-4 w-4 mr-1" /> Report Threat</Button>
            </DialogTrigger>
            <DialogContent className="bg-background border-border rounded-none">
              <DialogHeader>
                <DialogTitle className="font-heading tracking-tight">Report a Threat</DialogTitle>
                <DialogDescription className="text-xs text-muted-foreground">Critical & high severity threats instantly email your response team.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div><Label className="text-xs text-muted-foreground">Threat name</Label><Input data-testid="threat-name" className="rounded-none bg-secondary/40 border-border mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs text-muted-foreground">Severity</Label>
                    <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                      <SelectTrigger data-testid="threat-severity" className="rounded-none bg-secondary/40 border-border mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>{["critical", "high", "medium", "low"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs text-muted-foreground">Target workload</Label>
                    <Select value={form.workload} onValueChange={(v) => setForm({ ...form, workload: v })}>
                      <SelectTrigger data-testid="threat-workload" className="rounded-none bg-secondary/40 border-border mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{workloads.map((w) => <SelectItem key={w.id} value={w.name}>{w.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label className="text-xs text-muted-foreground">Description</Label><Textarea data-testid="threat-description" className="rounded-none bg-secondary/40 border-border mt-1.5" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              </div>
              <DialogFooter><Button data-testid="threat-save" onClick={report} disabled={saving} className="rounded-none bg-primary hover:bg-primary/90 text-white">{saving ? "Reporting…" : "Report & Alert"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </PageHeader>
      <div className="p-8 space-y-8">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="card-elevated p-6"><span className="text-xs text-muted-foreground uppercase tracking-widest">Active</span><div className="font-heading text-3xl font-bold tracking-tighter mt-3 text-primary">{active.length}</div></div>
          <div className="card-elevated p-6"><span className="text-xs text-muted-foreground uppercase tracking-widest">Critical</span><div className="font-heading text-3xl font-bold tracking-tighter mt-3 text-primary">{active.filter((t) => t.severity === "critical").length}</div></div>
          <div className="card-elevated p-6"><span className="text-xs text-muted-foreground uppercase tracking-widest">Resolved</span><div className="font-heading text-3xl font-bold tracking-tighter mt-3 text-[hsl(var(--chart-2))]">{rows.filter((t) => t.status === "resolved").length}</div></div>
        </div>

        <div className="space-y-3" data-testid="threats-list">
          {rows.map((t) => (
            <div key={t.id} className="card-elevated rounded-sm p-5 flex items-start justify-between gap-4">
              <div className="flex items-start gap-4">
                {t.status === "active" ? <AlertTriangle className="h-5 w-5 text-primary shrink-0 mt-0.5" /> : <CheckCircle2 className="h-5 w-5 text-[hsl(var(--chart-2))] shrink-0 mt-0.5" />}
                <div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-sm font-medium">{t.name}</span>
                    <StatusBadge value={t.severity} />
                    <StatusBadge value={t.status} />
                  </div>
                  <p className="text-sm text-muted-foreground mt-1.5">{t.description}</p>
                  <p className="font-mono text-[10px] text-muted-foreground mt-2 tracking-widest">TARGET: {t.workload} · {new Date(t.detected_at).toLocaleString()}</p>
                </div>
              </div>
              {canResolve && t.status === "active" && (
                <Button size="sm" onClick={() => resolve(t.id)} data-testid={`resolve-btn-${t.id}`} className="rounded-none bg-secondary hover:bg-primary hover:text-white text-xs shrink-0">
                  Resolve
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
